import {
  ProfessionalAgendaResponseDto,
  ProfessionalAgendaSlotsResponseDto,
  ProfessionalCustomScheduleCreationDto,
  ProfessionalCustomScheduleResponseDto,
  ProfessionalCustomScheduleUpdateDto,
  SearchAgendaDto,
  SearchConfusingAgendaDto,
} from '@docbay/schemas';

import axiosInstance from 'integration/api/axiosInstance';

/**
 * API service for agenda-related operations.
 */
export const AgendaAPI = {
  /**
   * Searches for agendas based on provided data.
   * @param {SearchAgendaDto} data - The search criteria to be used.
   * @returns {Promise<ProfessionalAgendaResponseDto[]>} A promise that resolves with an array of professional agenda response DTOs.
   */
  search: (data: SearchAgendaDto) => {
    return axiosInstance.post<ProfessionalAgendaResponseDto[]>(
      '/agendas/search',
      data,
    );
  },

  /**
   * Searches for agenda slots based on provided data.
   * @param {SearchAgendaDto} data - The search criteria to be used.
   * @returns {Promise<ProfessionalAgendaSlotsResponseDto>} A promise that resolves with a professional agenda slots response DTO.
   */
  searchSlots: (data: SearchAgendaDto) =>
    axiosInstance.post<ProfessionalAgendaSlotsResponseDto>(
      '/agendas/search-slots',
      data,
    ),

  /**
   * Searches for confusing agendas based on provided data.
   * @param {SearchConfusingAgendaDto} data - The search criteria to be used.
   * @returns {Promise<void>} A promise that resolves when the operation is complete.
   */
  fetchConfusingAgenda: (data: SearchConfusingAgendaDto) =>
    axiosInstance.post('/agendas/search-confusing-agenda', data),

  /**
   * Fetches open agenda for a professional based on provided ID and parameters.
   * @param {string} id - The professional's unique identifier.
   * @param {{ date: string; clinicId: string }} params - Additional parameters required to fetch the open agenda.
   * @returns {Promise<ProfessionalCustomScheduleResponseDto>} A promise that resolves with a professional custom schedule response DTO.
   */
  fetchOpenAgenda: (id: string, params: { date: string; clinicId: string }) => {
    return axiosInstance.get<ProfessionalCustomScheduleResponseDto>(
      `/professionals/${id}/custom-schedule`,
      {
        params,
      },
    );
  },

  /**
   * Adds an open agenda for a professional based on provided ID and data.
   * @param {string} id - The professional's unique identifier.
   * @param {ProfessionalCustomScheduleCreationDto} data - The data to be used to create the custom schedule.
   * @returns {Promise<void>} A promise that resolves when the operation is complete.
   */
  addOpenAgenda: (id: string, data: ProfessionalCustomScheduleCreationDto) => {
    return axiosInstance.post(`/professionals/${id}/custom-schedule`, data);
  },

  /**
   * Edits an open agenda for a professional based on provided ID and custom schedule ID.
   * @param {string} id - The professional's unique identifier.
   * @param {string} customScheduleId - The unique identifier of the custom schedule to be updated.
   * @param {ProfessionalCustomScheduleUpdateDto} hoursData - The data to be used to update the custom schedule.
   * @returns {Promise<void>} A promise that resolves when the operation is complete.
   */
  editOpenAgenda: (
    id: string,
    customScheduleId: string,
    hoursData: ProfessionalCustomScheduleUpdateDto,
  ) => {
    return axiosInstance.patch(
      `/professionals/${id}/custom-schedule/${customScheduleId}`,
      hoursData,
    );
  },

  /**
   * Removes an open agenda for a professional based on provided ID and custom schedule ID.
   * @param {string} id - The professional's unique identifier.
   * @param {string} customScheduleId - The unique identifier of the custom schedule to be removed.
   * @param {string} clinicId - The unique identifier of the clinic associated with the custom schedule.
   * @returns {Promise<void>} A promise that resolves when the operation is complete.
   */
  removeOpenAgenda: (
    id: string,
    customScheduleId: string,
    clinicId: string,
  ) => {
    return axiosInstance.delete(
      `/professionals/${id}/custom-schedule/${customScheduleId}/${clinicId}`,
    );
  },
};
